<script>
  import { loadStripe } from '@stripe/stripe-js';
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  // import { authFetch } from '../authService';
  import { getStripeUrl } from '../api';

  export let id; // Plan ID from router

  // Replace with your Publishable Key
  const STRIPE_KEY = getStripeUrl();
  const stripePromise = loadStripe(STRIPE_KEY);

  let stripe;
  let elements;
  let isLoading = true;
  let isProcessing = false;
  let error = '';
  
  let currentUser = null;
  let planDetails = null;

  authStore.subscribe(value => {
    currentUser = value.user;
  });

  onMount(async () => {
    stripe = await stripePromise;
    await fetchPlanDetails();
    // Automatically create intent once plan is loaded
    if (planDetails) {
        await createPaymentIntent();
    }
  });

  async function fetchPlanDetails() {
    try {
      // We assume the list endpoint can filter by ID or we just fetch all and find one. 
      // For efficiency, usually better to have a specific GET endpoint, 
      // but here we filter the list to reuse existing handlers if needed.
      // Ideally: GET /business-plans/:id (You might need to add this handler if not exists, 
      // or just rely on the CreateIntent backend logic to validate ID).
      
      // Let's just trust the ID for the UI display, the backend validates the cost.
      // We can fetch the list to get the name/price for display.
      const res = await fetch(getApiUrl(`/business-plans`)); // Get all to find one
      const plans = await res.json();
      planDetails = plans.find(p => p._id === id || p.id === id);
      
      if (!planDetails) throw new Error("Plan not found.");
    } catch (e) {
      error = e.message;
    }
  }

  async function createPaymentIntent() {
    try {
        const res = await fetch(getApiUrl('/create-biz-payment-intent'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ planId: id })
        });
        
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);

        elements = stripe.elements({ clientSecret: data.clientSecret });
        const paymentElement = elements.create('payment');
        
        await tick();
        paymentElement.mount('#payment-element');
        isLoading = false;

    } catch(e) {
        error = e.message;
        isLoading = false;
    }
  }

  async function confirmPayment() {
      if (!stripe || !elements) return;
      
      isProcessing = true;
      error = '';

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.origin + '/business-profile' },
          redirect: 'if_required'
      });

      if (stripeError) {
          error = stripeError.message;
          isProcessing = false;
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          try {
            // 1. Confirm to backend (Record Order)
            const confirmRes = await fetch(getApiUrl('/business-plans/orders'), {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ 
                    userId: currentUser.idTokenClaims.oid,
                    displayName: currentUser.name,
                    planId: id,
                    paymentIntentId: paymentIntent.id,
                    paidAmount: paymentIntent.amount / 100, // Convert back to dollars
                    paidCurrency: paymentIntent.currency
                })
            });

            if(!confirmRes.ok) throw new Error("Order recording failed.");

            // 2. --- THE FIX: RE-FETCH PROFILE & UPDATE STORE ---
            // Instead of authStore.set(), we fetch the fresh profile from DB
            // which now has 'isBusinessAccount: true'.
            const profileRes = await fetch(getApiUrl(`/users/profile/${currentUser.idTokenClaims.oid}`));
            if (profileRes.ok) {
                const newProfile = await profileRes.json();
                // Use the correct method to update the store
                authStore.setUserAndProfile(currentUser, newProfile);
            }

            // 3. Redirect
            navigate('/business-profile');

          } catch (e) {
              error = "Payment succeeded, but account activation failed: " + e.message;
              isProcessing = false;
          }
      }
  }
</script>

<div class="payment-container">
    <h2>Secure Checkout</h2>
    
    {#if error}
        <div class="error-alert">{error}</div>
    {/if}

    {#if planDetails}
        <div class="summary">
            <h3>{planDetails.name}</h3>
            <p class="price">{planDetails.currency} {planDetails.amount}</p>
            <p class="desc">{planDetails.creditsOnPlan} Credits / {planDetails.billingFrequency}</p>
        </div>
    {/if}

    <div class="card-element-wrapper">
        <!-- Stripe Element mounts here -->
        <div id="payment-element"></div>
    </div>
    
    {#if isLoading}
        <p>Loading payment options...</p>
    {:else}
        <button class="pay-btn" on:click={confirmPayment} disabled={isProcessing}>
            {isProcessing ? 'Processing...' : 'Subscribe & Activate'}
        </button>
        <button class="cancel-btn" on:click={() => navigate('/business-plans')} disabled={isProcessing}>
            Cancel
        </button>
    {/if}
</div>

<style>
    .payment-container { max-width: 500px; margin: 2rem auto; padding: 2rem; background: white; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
    h2 { text-align: center; margin-bottom: 1.5rem; }
    
    .summary { background: #f8f9fa; padding: 1rem; border-radius: 6px; margin-bottom: 2rem; text-align: center; border: 1px solid #eee; }
    .summary h3 { margin: 0 0 0.5rem 0; }
    .price { font-size: 1.5rem; font-weight: bold; color: #007bff; margin: 0; }
    .desc { color: #666; font-size: 0.9rem; }

    .card-element-wrapper { min-height: 200px; margin-bottom: 1.5rem; }
    
    .pay-btn { width: 100%; padding: 1rem; background-color: #28a745; color: white; border: none; border-radius: 6px; font-size: 1.1rem; font-weight: bold; cursor: pointer; margin-bottom: 0.5rem; }
    .pay-btn:disabled { background-color: #94d3a2; cursor: not-allowed; }
    
    .cancel-btn { width: 100%; padding: 0.8rem; background: none; border: 1px solid #ccc; color: #666; border-radius: 6px; cursor: pointer; }
    .cancel-btn:hover { background-color: #f1f1f1; }

    .error-alert { background-color: #fff3f3; color: #dc3545; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; border: 1px solid #ffc9c9; }
</style>